/**
 * Closed vocabularies for the SelectorPack.
 *
 * SECURITY BOUNDARY — read this before adding anything here.
 *
 * A SelectorPack is *data* published by an admin and pushed to every client
 * agent over WebSocket. A fixed interpreter in the agent reads it. Therefore the
 * only things a pack may express are **locators, conditions and scalar tuning
 * knobs**. Every enum in this file is closed: a pack containing a value that is
 * not listed here is rejected outright, on both the server (at publish time) and
 * the agent (at apply time).
 *
 * What must NEVER appear in this vocabulary:
 *   - a JavaScript source string of any kind (it would turn the config channel
 *     into remote code execution on every client machine),
 *   - a file path, shell command, URL to fetch, or anything that names a
 *     resource outside the page being inspected,
 *   - a free-form regular expression evaluated on the client (ReDoS surface).
 *
 * The *flow* of automation (open search → type keyword → scroll → open post) is
 * versioned code in `apps/agent/src/automation/facebook/adapters/`. Only the
 * selectors it consumes are data. Changing the flow requires a new adapter
 * version and a rebuilt agent; changing a selector does not.
 */

/** Playwright locator strategies the interpreter is allowed to build. */
export const LOCATOR_STRATEGIES = [
  'role',
  'testId',
  'label',
  'placeholder',
  'altText',
  'title',
  'text',
  'css',
  'xpath',
] as const;

export type LocatorStrategy = (typeof LOCATOR_STRATEGIES)[number];

/**
 * ARIA roles accepted by `getByRole`. Restricted to the roles that actually occur
 * in the Facebook surfaces we drive, so a typo fails validation instead of
 * silently producing a locator that never matches.
 */
export const ARIA_ROLES = [
  'article',
  'banner',
  'button',
  'checkbox',
  'combobox',
  'dialog',
  'feed',
  'form',
  'heading',
  'img',
  'link',
  'list',
  'listitem',
  'main',
  'menu',
  'menuitem',
  'navigation',
  'option',
  'presentation',
  'progressbar',
  'radio',
  'region',
  'search',
  'searchbox',
  'separator',
  'status',
  'tab',
  'tablist',
  'tabpanel',
  'textbox',
  'toolbar',
] as const;

export type AriaRole = (typeof ARIA_ROLES)[number];

/**
 * How a matched element is turned into a value.
 *
 * `labelledByText` deserves a word, because it is the one mode that reads
 * outside the matched element. Facebook renders the post timestamp as ~60
 * one-character `<span>`s interleaved with decoy characters that CSS moves out
 * of view with `position:absolute` — not `display:none` — so `text` and
 * `innerText` both return scrambled garbage. The readable value exists only in
 * the element named by `aria-labelledby`, which the page keeps in a hidden
 * container at `<body>` level, outside the result card.
 *
 * It grants the pack no new power: it reads one attribute and then looks up one
 * element by its id. Still no script evaluation, no network, no file access —
 * the boundary described above is intact.
 */
export const EXTRACT_MODES = [
  'text',
  'innerText',
  'href',
  'src',
  'attribute',
  'datetimeAttribute',
  'ariaLabel',
  'labelledByText',
  /**
   * Reconstruct the USER-VISIBLE string of an element whose text Facebook
   * scrambles: dozens of single-character spans whose DOM order is shuffled and
   * whose true order is re-established with CSS. The adapter reads each visible
   * character's rendered position and sorts by it — geometry, not code
   * evaluation, and still read-only. Introduced with adapter v2 for the
   * timestamp links that carry no accessible name.
   */
  'visualText',
  /**
   * `labelledByText` after RESTING THE POINTER on the element (adapter v3).
   *
   * The 2026-08 group-surface cards render the timestamp as SVG paths with no
   * text at all, and wire `aria-labelledby` to the premounted tooltip label
   * only when a pointer arrives — the static DOM has no programmatic link.
   * Hovering is the same passive gesture a human reader makes to see the
   * time; the adapter never clicks, types or submits through this mode, so
   * the "config is data, not code" boundary is intact.
   */
  'hoverLabelledByText',
  /**
   * Follow an SVG `<use href="#id">` reference and read the text of the element
   * it points to (adapter v4).
   *
   * The 2026-08 cards draw the timestamp as `<svg><use>` referencing a
   * `<text id="…">` node that Facebook parks — WITH the clean label already in
   * it — in the page-level `<defs>`. The reference is static, so unlike
   * `hoverLabelledByText` this read needs no pointer and cannot miss a drifting
   * card. Same power envelope as `labelledByText`: one attribute read, one
   * lookup by id — no script, no network, the boundary above is intact.
   */
  'svgUseText',
  'exists',
  'count',
] as const;

export type ExtractMode = (typeof EXTRACT_MODES)[number];

/** Post-processing steps applied to an extracted raw string, in order. */
export const FIELD_TRANSFORMS = [
  'trim',
  'normalizeWhitespace',
  'stripEmoji',
  'toAbsoluteUrl',
  'stripUrlTrackingParams',
  'parseInteger',
  'parseCompactCount',
  'parseIsoDate',
  'parseUnixSeconds',
  'parseRelativeTime',
  'extractFacebookPostId',
  /** Shortcode from a Threads permalink (`/@user/post/<code>`). Same contract as
   *  `extractFacebookPostId`: a pure string→string function in the interpreter's
   *  registry — a pack names it, versioned code implements it. */
  'extractThreadsPostId',
  'lowercase',
] as const;

export type FieldTransform = (typeof FIELD_TRANSFORMS)[number];

/** Detector condition kinds. Deliberately free of client-side regex evaluation. */
export const CONDITION_TYPES = [
  'urlContains',
  'urlPathStartsWith',
  'titleContains',
  'textVisible',
  'selectorVisible',
  'selectorAbsent',
  'anyOf',
  'allOf',
  'not',
] as const;

export type ConditionType = (typeof CONDITION_TYPES)[number];

/**
 * Named locator rules the adapter looks up. Adding a key here is an adapter-code
 * change (the interpreter must know what to do with it), which is exactly why
 * the list is closed.
 */
export const SELECTOR_RULE_KEYS = [
  // Search flow
  'searchEntryPoint',
  'searchBox',
  'searchSubmit',
  'searchFilterPosts',
  /** The "Bài viết mới đây" switch in the filter panel. The cycle primarily
   *  turns recency on through the URL; when a pack defines this rule the agent
   *  also verifies/clicks the visible switch. */
  'searchFilterRecent',
  'searchResultsContainer',
  'resultCard',
  'resultSeeMore',
  'endOfResultsMarker',

  // Post surfaces
  'postContainer',
  'postPermalink',
  'postAuthorLink',
  'postTimestamp',
  'postSeeMore',
  'postReactionCount',
  'postCommentCount',
  'postShareCount',
  'postMediaImage',
  'postGroupName',

  // Comment surfaces
  'commentBox',
  'commentSubmit',
  'commentImageAttach',
  'commentSuccessMarker',

  // Session surfaces
  'loginForm',
  'profileAvatar',
  /** The signed-in account's visible name in the page chrome. Read once per
   *  session probe, best-effort. Display identity only — never cookies. */
  'accountDisplayName',
  'accountMenu',
  'cookieConsentAccept',
] as const;

export type SelectorRuleKey = (typeof SELECTOR_RULE_KEYS)[number];

/** Named detectors the adapter evaluates to derive session/browser state. */
export const DETECTOR_KEYS = [
  'loginRequired',
  'sessionActive',
  'checkpoint',
  'twoFactorRequired',
  'accountRestricted',
  'rateLimited',
  'searchReady',
  'contentUnavailable',
] as const;

export type DetectorKey = (typeof DETECTOR_KEYS)[number];

/** Post fields the declarative parser can extract from a result card. */
export const POST_FIELD_KEYS = [
  'externalPostId',
  'permalink',
  'content',
  'authorName',
  'authorUrl',
  'authorAvatarUrl',
  'postedAt',
  'reactionCount',
  'commentCount',
  'shareCount',
  'mediaUrls',
  'groupName',
] as const;

export type PostFieldKey = (typeof POST_FIELD_KEYS)[number];

/**
 * Supported platforms, lowercase — the id a pack document declares.
 *
 * Mirrored (uppercase) by `PLATFORM_IDS` in `@midiautolistening/contracts`
 * (`platform-catalog.ts`), which owns the mapping between the two spellings.
 * This package cannot depend on contracts, so the agreement is asserted by a
 * contracts-side test rather than by an import.
 */
export const SELECTOR_PACK_PLATFORMS = ['facebook', 'threads'] as const;
export type SelectorPackPlatform = (typeof SELECTOR_PACK_PLATFORMS)[number];

/** Locales that ship a baseline pack with the agent. */
export const SELECTOR_PACK_LOCALES = ['vi-VN', 'en-US'] as const;
export type SelectorPackLocale = (typeof SELECTOR_PACK_LOCALES)[number];

/** Current schema version of the pack document itself. */
export const SELECTOR_PACK_SCHEMA_VERSION = 1 as const;

/** Hard limits — a malformed or hostile pack must not be able to hang an agent. */
export const SELECTOR_PACK_LIMITS = {
  maxCandidatesPerRule: 8,
  maxSelectorLength: 512,
  maxTextLength: 256,
  maxConditionDepth: 4,
  maxConditionChildren: 8,
  maxTransformsPerField: 6,
} as const;
